/**
 * Playwright PDF 印刷模式专用页面 — 企业跨班级报告 (R2)
 *
 * 路由：/print/enterprise/:id?token=<hmac>
 *
 * 与 PrintClassReportPage 同款骨架（公共路由，不嵌 PrivateRoute；HMAC token 承担鉴权；
 * id='preview'/'demo' 走本地 mock；双 rAF + 300ms 后置 data-report-ready 给 Playwright），
 * 仅 print-data endpoint 与渲染组件不同。
 */
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import EnterpriseReport from '../components/report_enterprise/EnterpriseReport';
import { mockEnterpriseReportPayload } from '../mocks/enterpriseReportMock';
import '../styles/print.css';
import '../components/report_enterprise/enterprise-report.css';

async function fetchEnterprisePrintData(id, token) {
  const url = `/api/enterprises/${encodeURIComponent(id)}/print-data/?token=${encodeURIComponent(token)}`;
  const resp = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'omit',
  });
  let body = null;
  try {
    body = await resp.json();
  } catch (_) {
    body = null;
  }
  return { ok: resp.ok, status: resp.status, body };
}

export default function PrintEnterpriseReportPage() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const id = params.id || 'preview';
  const token = searchParams.get('token') || '';

  const [payload, setPayload] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const isMock = id === 'preview' || id === 'demo';
      if (isMock) {
        await new Promise((r) => setTimeout(r, 50));
        if (!cancelled) setPayload(mockEnterpriseReportPayload);
        return;
      }
      if (!token) {
        setErrorMsg('缺少 token 参数。印刷模式 URL 形如 /print/enterprise/<id>?token=<hmac>。');
        return;
      }
      const { ok, status, body } = await fetchEnterprisePrintData(id, token);
      if (cancelled) return;
      if (!ok) {
        const detail = body?.detail || `HTTP ${status}`;
        setErrorMsg(`拉 print-data 失败 (${status}): ${detail}`);
        return;
      }
      setPayload(body);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [id, token]);

  useEffect(() => {
    if (!payload) return undefined;
    let raf1 = 0;
    let raf2 = 0;
    let timer = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        timer = window.setTimeout(() => {
          document.documentElement.dataset.reportReady = '1';
          window.__reportReady = true;
        }, 300);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      if (timer) window.clearTimeout(timer);
      delete document.documentElement.dataset.reportReady;
      window.__reportReady = false;
    };
  }, [payload]);

  if (errorMsg) {
    return (
      <div style={{ padding: 40, fontFamily: 'sans-serif' }}>
        <h2>印刷模式暂未就绪</h2>
        <p>{errorMsg}</p>
      </div>
    );
  }
  if (!payload) {
    return <div style={{ padding: 40, fontFamily: 'sans-serif' }}>正在加载报告…</div>;
  }

  return <EnterpriseReport payload={payload} printMode />;
}
